/**
 * Karaoke studio audio engine.
 *
 * Everything here runs in the browser: the droplet already carries MySQL, the
 * API and ~1,900 cron launches a day, so mixing must not land on it.
 *
 * The chain is mono end to end. Mono keeps the WAV upload at ~5.3 MB/minute
 * (a stereo 44.1k mix is double that, and most singers here are on phones), and
 * it sidesteps the comb filtering you get from summing a stereo reverb down.
 */

/* -------------------------------------------------------------- presets */

// wet: how much reverb is mixed in. decay/predelay shape the impulse response.
// air: presence lift around 3.2kHz — a little goes a long way on phone mics.
export const EFFECT_PRESETS = {
  dry: { label: 'Dry', hint: 'No room. Your voice, untouched.', decay: 0, predelay: 0, wet: 0, air: 0 },
  room: { label: 'Room', hint: 'Small, close, natural.', decay: 0.45, predelay: 0.008, wet: 0.18, air: 2 },
  studio: { label: 'Studio', hint: 'Tight booth with a polished tail.', decay: 0.9, predelay: 0.018, wet: 0.26, air: 3.5 },
  hall: { label: 'Hall', hint: 'Big and reverberant. Good for slow songs.', decay: 2.2, predelay: 0.035, wet: 0.34, air: 3 },
  stage: { label: 'Stage', hint: 'Live feel with a short slap-back.', decay: 1.3, predelay: 0.05, wet: 0.3, air: 4 },
};

export const DEFAULT_PRESET = 'studio';

/**
 * Impulse responses are generated, not sampled. Every free IR pack worth using
 * carries an attribution licence (OpenAIR is CC-BY), and shipping a licence
 * obligation inside a bundle for a reverb tail is not worth it. Exponentially
 * decaying noise is what most of those packs approximate anyway.
 */
export function buildImpulseResponse(ctx, preset) {
  const { decay, predelay } = preset;
  if (!decay) return null;

  const rate = ctx.sampleRate;
  const length = Math.max(1, Math.floor(rate * (decay + predelay)));
  const ir = ctx.createBuffer(1, length, rate);
  const data = ir.getChannelData(0);
  const silent = Math.floor(rate * predelay);

  // Deterministic noise: a mulberry32 PRNG so the same preset always renders
  // the same tail, which matters when someone re-exports the same take.
  let seed = 0x9e3779b9;
  const random = () => {
    seed = (seed + 0x6d2b79f5) >>> 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return (((t ^ (t >>> 14)) >>> 0) / 4294967296) * 2 - 1;
  };

  for (let i = silent; i < length; i += 1) {
    const progress = (i - silent) / (length - silent);
    // Damp the tail as it decays so it darkens rather than hissing out.
    data[i] = random() * Math.pow(1 - progress, 2.2) * (1 - progress * 0.35);
  }
  return ir;
}

/* --------------------------------------------------------------- decode */

export async function decodeFromUrl(ctx, url) {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) throw new Error(`Could not load track (${response.status})`);
  const bytes = await response.arrayBuffer();
  // Safari still wants the callback form of decodeAudioData.
  return new Promise((resolve, reject) => {
    ctx.decodeAudioData(bytes, resolve, reject);
  });
}

/** Float32 blocks from the worklet -> one contiguous buffer. */
export function concatBlocks(blocks, sampleRate, ctx) {
  const total = blocks.reduce((sum, block) => sum + block.length, 0);
  const buffer = (ctx || new OfflineAudioContext(1, Math.max(1, total), sampleRate))
    .createBuffer(1, Math.max(1, total), sampleRate);
  const data = buffer.getChannelData(0);
  let offset = 0;
  for (const block of blocks) {
    data.set(block, offset);
    offset += block.length;
  }
  return buffer;
}

/* -------------------------------------------------- chain latency probe */

const latencyCache = new Map();

/**
 * The vocal runs through a compressor and the backing track does not, and
 * DynamicsCompressorNode adds a fixed pre-delay — 6ms in Chrome, unspecified
 * elsewhere. Left alone it drags every vocal that far behind the beat.
 *
 * Rather than hardcode one browser's number, send an impulse through the same
 * chain and see where it comes out. Costs a fraction of a second, cached per
 * sample rate.
 */
async function measureVocalChainLatency(rate) {
  if (latencyCache.has(rate)) return latencyCache.get(rate);

  const probeAt = 0.1;
  const ctx = new OfflineAudioContext(1, Math.ceil(rate * 0.5), rate);
  const impulse = ctx.createBuffer(1, Math.ceil(rate * 0.5), rate);
  impulse.getChannelData(0)[Math.floor(probeAt * rate)] = 1;

  const source = ctx.createBufferSource();
  source.buffer = impulse;

  const highpass = ctx.createBiquadFilter();
  highpass.type = 'highpass';
  highpass.frequency.value = 85;
  const presence = ctx.createBiquadFilter();
  presence.type = 'peaking';
  presence.frequency.value = 3200;
  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -20;
  comp.knee.value = 24;
  comp.ratio.value = 3;
  comp.attack.value = 0.005;
  comp.release.value = 0.18;

  source.connect(highpass).connect(presence).connect(comp).connect(ctx.destination);
  source.start(0);

  const rendered = await ctx.startRendering();
  const data = rendered.getChannelData(0);
  let peakIndex = -1;
  let peak = 0;
  for (let i = 0; i < data.length; i += 1) {
    const value = Math.abs(data[i]);
    if (value > peak) { peak = value; peakIndex = i; }
  }

  const latency = peakIndex < 0 ? 0 : Math.max(0, peakIndex / rate - probeAt);
  // Anything beyond 50ms means the probe found the wrong thing; ignore it
  // rather than shifting the vocal somewhere absurd.
  const safe = latency > 0.05 ? 0 : latency;
  latencyCache.set(rate, safe);
  return safe;
}

/* ------------------------------------------------------------------ mix */

/**
 * Render the finished take.
 *
 * `syncMs` shifts the vocal against the music. Negative pulls it earlier, which
 * is the normal direction: the singer hears the backing track a little late
 * (output latency) and their voice reaches the mic a little late again, so a
 * raw take always sits behind the beat. Without this control every recording
 * sounds sloppy and singers blame themselves.
 *
 * `musicLeadSec` is where the backing track starts inside the vocal recording —
 * the page records through the count-in, so the two are on one timeline.
 */
export async function renderMix({
  vocalBuffer,
  musicBuffer,
  musicLeadSec = 0,
  syncMs = 0,
  presetKey = DEFAULT_PRESET,
  vocalGain = 1,
  musicGain = 0.85,
}) {
  const preset = EFFECT_PRESETS[presetKey] || EFFECT_PRESETS[DEFAULT_PRESET];
  const rate = vocalBuffer.sampleRate;

  // Music sits at t=0 of the output. The vocal starts musicLeadSec before it,
  // plus whatever alignment the singer dialled in, minus the delay its own
  // processing chain will add downstream.
  const chainLatency = await measureVocalChainLatency(rate);
  const vocalOffsetSec = -musicLeadSec + syncMs / 1000 - chainLatency;
  const vocalStart = Math.max(0, vocalOffsetSec);
  const vocalSkip = Math.max(0, -vocalOffsetSec);
  const vocalPlayable = Math.max(0, vocalBuffer.duration - vocalSkip);

  const durationSec = Math.max(
    musicBuffer ? musicBuffer.duration : 0,
    vocalStart + vocalPlayable
  );
  if (!durationSec) throw new Error('Nothing to mix');

  const ctx = new OfflineAudioContext(1, Math.ceil(durationSec * rate), rate);
  const master = ctx.createGain();
  master.gain.value = 1;

  // Catch anything that peaks — singers push hard into phone mics and a clipped
  // export is unrecoverable. DynamicsCompressorNode is not a brickwall limiter,
  // though: with a 2ms attack the leading transient slips through and measured
  // just over full scale. The trim after it is what actually guarantees
  // headroom, and it is why the threshold sits at -3 rather than -1.5.
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -3;
  limiter.knee.value = 0;
  limiter.ratio.value = 20;
  limiter.attack.value = 0.002;
  limiter.release.value = 0.15;

  const trim = ctx.createGain();
  trim.gain.value = 0.92;

  master.connect(limiter).connect(trim).connect(ctx.destination);

  if (musicBuffer) {
    const music = ctx.createBufferSource();
    music.buffer = musicBuffer;
    const gain = ctx.createGain();
    gain.gain.value = musicGain;
    music.connect(gain).connect(master);
    music.start(0);
  }

  const vocal = ctx.createBufferSource();
  vocal.buffer = vocalBuffer;

  // Rumble, handling noise and phone-mic proximity boom all live below 85Hz.
  const highpass = ctx.createBiquadFilter();
  highpass.type = 'highpass';
  highpass.frequency.value = 85;

  const presence = ctx.createBiquadFilter();
  presence.type = 'peaking';
  presence.frequency.value = 3200;
  presence.Q.value = 0.9;
  presence.gain.value = preset.air;

  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -20;
  comp.knee.value = 24;
  comp.ratio.value = 3;
  comp.attack.value = 0.005;
  comp.release.value = 0.18;

  const level = ctx.createGain();
  level.gain.value = vocalGain;

  vocal.connect(highpass).connect(presence).connect(comp).connect(level);

  const ir = buildImpulseResponse(ctx, preset);
  if (ir) {
    const dry = ctx.createGain();
    dry.gain.value = 1 - preset.wet * 0.5;
    const wet = ctx.createGain();
    wet.gain.value = preset.wet;
    const convolver = ctx.createConvolver();
    convolver.buffer = ir;
    convolver.normalize = true;

    level.connect(dry).connect(master);
    level.connect(convolver).connect(wet).connect(master);
  } else {
    level.connect(master);
  }

  vocal.start(vocalStart, vocalSkip);
  return ctx.startRendering();
}

/* -------------------------------------------------------------- encoding */

/**
 * 16-bit mono WAV. No MP3 encoder is bundled on purpose — the usable JS ones
 * are LAME ports under the LGPL, and the API transcodes to MP3 with ffmpeg the
 * moment the file lands anyway.
 */
export function encodeWav(buffer) {
  const samples = buffer.getChannelData(0);
  const rate = buffer.sampleRate;
  const bytes = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(bytes);

  const writeText = (offset, text) => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
  };

  writeText(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeText(8, 'WAVE');
  writeText(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);       // PCM
  view.setUint16(22, 1, true);       // mono
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeText(36, 'data');
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i += 1) {
    const clamped = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
    offset += 2;
  }
  return new Blob([view], { type: 'audio/wav' });
}

/**
 * Best guess at how far behind the beat a raw take will sit: the singer hears
 * the track late by the output latency, and their voice reaches the recorder
 * late again. Chrome reports outputLatency; Safari does not, so fall back to
 * baseLatency plus a typical mobile input figure. The slider exists because
 * this is only ever an estimate.
 */
export function estimateSyncMs(ctx) {
  const output = typeof ctx.outputLatency === 'number' && ctx.outputLatency > 0
    ? ctx.outputLatency
    : (ctx.baseLatency || 0.01) * 2;
  const assumedInput = 0.03;
  return -Math.round((output + assumedInput) * 1000);
}

export function formatClock(seconds) {
  const total = Math.max(0, Math.floor(seconds || 0));
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}
